// utils/index.ts - Fonctions utilitaires pour le traitement de la paie

/**
 * Formate un montant en devise locale
 */
export const formatCurrency = (
  amount: number,
  currency: string = "XOF"
): string => {
  if (isNaN(amount)) return "0 " + currency;

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Formate une date en format local
 */
export const formatDate = (
  date: string | Date,
  format: string = "short"
): string => {
  if (!date) return "-";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "-";

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: format === "long" ? "long" : "2-digit",
    day: "2-digit",
  };

  return new Intl.DateTimeFormat("fr-FR", options).format(dateObj);
};

/**
 * Formate une période de dates
 */
export const formatPeriod = (
  startDate: string | Date,
  endDate: string | Date
): string => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  return `${start} - ${end}`;
};

/**
 * Calcule le nombre de jours entre deux dates
 */
export const getDaysBetween = (
  startDate: string | Date,
  endDate: string | Date
): number => {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Valide une période de dates
 */
export const validatePeriod = (
  startDate: string | Date,
  endDate: string | Date
): string | null => {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  if (isNaN(start.getTime())) return "Date de début invalide";
  if (isNaN(end.getTime())) return "Date de fin invalide";
  if (start >= end)
    return "La date de début doit être antérieure à la date de fin";

  const days = getDaysBetween(start, end);
  if (days > 60) return "La période ne peut pas dépasser 60 jours";
  if (days < 1) return "La période doit contenir au moins 1 jour";

  return null;
};

/**
 * Formate un pourcentage
 */
export const formatPercentage = (
  value: number,
  decimals: number = 2
): string => {
  if (isNaN(value)) return "0%";
  return `${value.toFixed(decimals)}%`;
};

/**
 * Génère un nom de période automatique
 */
export const generatePeriodName = (
  startDate: string | Date,
  endDate: string | Date
): string => {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  // Si c'est le même mois
  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${monthNames[start.getMonth()]} ${start.getFullYear()}`;
  }

  // Si c'est la même année
  if (start.getFullYear() === end.getFullYear()) {
    return `${monthNames[start.getMonth()]} - ${
      monthNames[end.getMonth()]
    } ${start.getFullYear()}`;
  }

  // Années différentes
  return `${monthNames[start.getMonth()]} ${start.getFullYear()} - ${
    monthNames[end.getMonth()]
  } ${end.getFullYear()}`;
};

/**
 * Convertit un statut en couleur
 */
export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    draft: "#d9d9d9",
    processing: "#1890ff",
    calculated: "#faad14",
    partial_error: "#ff4d4f",
    validated: "#52c41a",
    closed: "#8c8c8c",
  };

  return colors[status] || colors.draft;
};

/**
 * Calcule la progression en pourcentage
 */
export const calculateProgress = (completed: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
};

/**
 * Valide un montant
 */
export const validateAmount = (amount: number | string): string | null => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(num)) return "Montant invalide";
  if (num < 0) return "Le montant ne peut pas être négatif";
  if (num > 999999999) return "Montant trop élevé";

  return null;
};

/**
 * Formate un numéro d'employé
 */
export const formatEmployeeNumber = (number: string | number): string => {
  if (!number) return "N/A";
  return `EMP-${String(number).padStart(4, "0")}`;
};

/**
 * Génère un nom complet d'employé
 */
export const formatEmployeeName = (
  firstName: string,
  lastName: string
): string => {
  if (!firstName && !lastName) return "Nom inconnu";
  if (!firstName) return lastName;
  if (!lastName) return firstName;
  return `${lastName.toUpperCase()}, ${firstName}`;
};

/**
 * Calcule l'âge à partir de la date de naissance
 */
export const calculateAge = (birthDate: string | Date): number => {
  const birth = typeof birthDate === "string" ? new Date(birthDate) : birthDate;
  const today = new Date();

  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

/**
 * Détermine si une période peut être modifiée
 */
export const canEditPeriod = (status: string): boolean => {
  return ["draft", "partial_error"].includes(status);
};

/**
 * Détermine si une période peut être supprimée
 */
export const canDeletePeriod = (status: string): boolean => {
  return status === "draft";
};

/**
 * Détermine si les calculs peuvent être lancés
 */
export const canLaunchCalculations = (status: string): boolean => {
  return ["draft", "partial_error", "variables_set"].includes(status);
};

/**
 * Détermine si les calculs peuvent être validés
 */
export const canValidateCalculations = (status: string): boolean => {
  return status === "calculated";
};

/**
 * Génère un nom de fichier d'export
 */
export const generateExportFileName = (
  periodName: string,
  type: string = "export"
): string => {
  const timestamp = new Date().toISOString().slice(0, 10);
  const cleanName = periodName.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
  return `paie_${cleanName}_${type}_${timestamp}`;
};

/**
 * Convertit une valeur en nombre sûr
 */
export const safeNumber = (value: any, defaultValue: number = 0): number => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Tronque un texte avec ellipses
 */
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + "...";
};

/**
 * Détermine la couleur du statut de progression
 */
export const getProgressStatus = (
  completed: number,
  total: number,
  errors: number
): "success" | "active" | "exception" | "normal" => {
  if (total === 0) return "normal";
  if (errors > 0) return "exception";
  if (completed === total) return "success";
  if (completed > 0) return "active";
  return "normal";
};

// Types pour les fonctions utilitaires
export interface PeriodValidation {
  isValid: boolean;
  errors: string[];
}

export interface EmployeeInfo {
  id: number;
  fullName: string;
  employeeNumber: string;
}

export interface AmountBreakdown {
  gross: number;
  deductions: number;
  net: number;
}

/**
 * Valide une période complète
 */
export const validatePeriodComplete = (
  name: string,
  startDate: string | Date,
  endDate: string | Date
): PeriodValidation => {
  const errors: string[] = [];

  if (!name || name.trim().length < 3) {
    errors.push("Le nom de la période doit contenir au moins 3 caractères");
  }

  const dateError = validatePeriod(startDate, endDate);
  if (dateError) {
    errors.push(dateError);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Filtre les employés selon des critères multiples
 */
export const filterEmployees = (
  employees: any[],
  filters: {
    search?: string;
    department?: number;
    position?: number;
    status?: string;
  }
): any[] => {
  return employees.filter((emp) => {
    // Filtre par recherche textuelle
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
      const employeeNumber = emp.employee_number?.toLowerCase() || "";
      const department = emp.department_name?.toLowerCase() || "";
      const position = emp.position_title?.toLowerCase() || "";

      if (
        !fullName.includes(searchLower) &&
        !employeeNumber.includes(searchLower) &&
        !department.includes(searchLower) &&
        !position.includes(searchLower)
      ) {
        return false;
      }
    }

    // Filtre par département
    if (filters.department && emp.department_id !== filters.department) {
      return false;
    }

    // Filtre par poste
    if (filters.position && emp.position_id !== filters.position) {
      return false;
    }

    // Filtre par statut
    if (filters.status && emp.status !== filters.status) {
      return false;
    }

    return true;
  });
};

/**
 * Groupe les employés par département
 */
export const groupEmployeesByDepartment = (
  employees: any[]
): Record<string, any[]> => {
  return employees.reduce((groups, emp) => {
    const deptName = emp.department_name || "Sans département";
    if (!groups[deptName]) {
      groups[deptName] = [];
    }
    groups[deptName].push(emp);
    return groups;
  }, {});
};

/**
 * Calcule les statistiques d'employés
 */
export const calculateEmployeeStats = (employees: any[]) => {
  const total = employees.length;
  const active = employees.filter((emp) => emp.status === "active").length;
  const inactive = employees.filter((emp) => emp.status === "inactive").length;

  const byDepartment = employees.reduce((acc, emp) => {
    const dept = emp.department_name || "Sans département";
    acc[dept] = (acc[dept] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return {
    total,
    active,
    inactive,
    byDepartment,
  };
};

/**
 * Valide la sélection d'employés pour une période
 */
export const validateEmployeeSelection = (
  selectedEmployeeIds: number[],
  allEmployees: any[]
): { isValid: boolean; warnings: string[]; errors: string[] } => {
  const warnings: string[] = [];
  const errors: string[] = [];

  if (selectedEmployeeIds.length === 0) {
    errors.push("Aucun employé sélectionné");
    return { isValid: false, warnings, errors };
  }

  const selectedEmployees = allEmployees.filter((emp) =>
    selectedEmployeeIds.includes(emp.id)
  );

  // Vérifier les employés inactifs
  const inactiveCount = selectedEmployees.filter(
    (emp) => emp.status !== "active"
  ).length;
  if (inactiveCount > 0) {
    warnings.push(`${inactiveCount} employé(s) inactif(s) sélectionné(s)`);
  }

  // Vérifier les employés sans contrat actif
  const noContractCount = selectedEmployees.filter(
    (emp) => !emp.contract_type
  ).length;
  if (noContractCount > 0) {
    warnings.push(`${noContractCount} employé(s) sans contrat actif`);
  }

  return {
    isValid: errors.length === 0,
    warnings,
    errors,
  };
};

// SQL counters may arrive as strings. Validated results are still completed work.
export const completedPayrollCount = (value: { completed_calculations?: number; validated_calculations?: number }) =>
  Number(value.completed_calculations || 0) + Number(value.validated_calculations || 0);
